import {
  type CalendarDate,
  type CalendarDateTime,
  fromDate,
  getLocalTimeZone,
  parseDate,
  parseDateTime,
  toCalendarDate,
  toCalendarDateTime,
} from "@internationalized/date";
import { displayLocale } from "./formatNumber";

// Comparisons parse via parseToDate — date-only as local midnight, naive datetime as local, "Z"/offset as the exact instant — matching how dayjs read these strings, then compare/arith on the resulting Date.

export function parseNaiveDate(value: string): CalendarDate | null {
  try {
    return value.includes("T") ? toCalendarDate(parseDateTime(value)) : parseDate(value);
  } catch {
    return null;
  }
}

export function parseNaiveDateTime(value: string): CalendarDateTime | null {
  try {
    return value.includes("T") ? parseDateTime(value) : toCalendarDateTime(parseDate(value));
  } catch {
    return null;
  }
}

/** Parse an ISO string as dayjs did: a date-only value as local midnight, otherwise the exact instant. */
export function parseToDate(value: string): Date | null {
  if (!value.includes("T")) {
    const d = parseNaiveDate(value);
    return d ? d.toDate(getLocalTimeZone()) : null;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** Whether a date ("YYYY-MM-DD") or datetime value is later than the current instant. */
export function isInFuture(value: string): boolean {
  const d = parseToDate(value);
  return d ? d.getTime() > Date.now() : false;
}

/** Whether `value` is strictly before `other`. */
export function isBefore(value: string, other: string): boolean {
  const a = parseToDate(value);
  const b = parseToDate(other);
  return a && b ? a.getTime() < b.getTime() : false;
}

/** Whether `value` is strictly after `other`. */
export function isAfter(value: string, other: string): boolean {
  const a = parseToDate(value);
  const b = parseToDate(other);
  return a && b ? a.getTime() > b.getTime() : false;
}

/** Whether `datetime` falls after the end of `date`'s (local) calendar day. */
export function isAfterEndOfDay(datetime: string, date: string): boolean {
  const dt = parseToDate(datetime);
  const d = parseToDate(date);
  if (!dt || !d) return false;
  const endOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  return dt.getTime() > endOfDay.getTime();
}

/** Whether `value`'s local calendar day is before today. */
export function isBeforeToday(value: string): boolean {
  const d = parseToDate(value);
  return d ? startOfLocalDay(d) < startOfLocalDay(new Date()) : false;
}

/** Whole minutes elapsed from `start` to `end`, truncated toward zero (NaN if unparseable). */
export function diffMinutes(start: string, end: string): number {
  const from = parseToDate(start);
  const to = parseToDate(end);
  if (!from || !to) return Number.NaN;
  return Math.trunc((to.getTime() - from.getTime()) / 60_000);
}

export function elapsedMinutes(start: string | null | undefined, end: string | null | undefined): number | null {
  if (!start || !end) return null;
  const minutes = diffMinutes(start, end);
  return Number.isNaN(minutes) || minutes < 0 ? null : minutes;
}

/** The instant `minutes` after `value` as an ISO-8601 UTC string (null if unparseable). */
export function addMinutesToInstant(value: string, minutes: number): string | null {
  const d = parseToDate(value);
  return d ? new Date(d.getTime() + minutes * 60_000).toISOString() : null;
}

/** Local wall-clock "YYYY-MM-DDTHH:mm" for a Date, e.g. to seed a datetime-local input. */
export function toDatetimeLocalString(date: Date): string {
  return toCalendarDateTime(fromDate(date, getLocalTimeZone())).toString().slice(0, 16);
}

export function formatInstantForCell(value: string, locale?: string): string {
  const date = parseToDate(value);
  if (!date) return "-";

  const parts = new Intl.DateTimeFormat(displayLocale(locale), {
    dateStyle: "short",
    timeStyle: "medium",
  }).formatToParts(date);

  return parts
    .map((part, index) =>
      part.type === "literal" && parts[index + 1]?.type === "dayPeriod"
        ? part.value.replace(/\s/g, "\u00a0")
        : part.value,
    )
    .join("");
}
